// src/pages/Appli/AppliAdmin/ListingClients/components/AssociationsFilters.jsx

import React from "react";

import {
  FaDesktop,
  FaFilter,
  FaSearch,
  FaTable,
  FaThLarge,
  FaTimes,
} from "react-icons/fa";

import {
  ASSOCIATION_STATUS_VALUES,
} from "../data/associations.data";

const SELECT_CLASS = [
  "w-full rounded-xl border",
  "border-slate-200 dark:border-slate-700",
  "bg-white dark:bg-slate-900",
  "px-3 py-2.5",
  "text-sm text-slate-800 dark:text-slate-100",
  "outline-none transition",
  "focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10",
].join(" ");

function FilterSelect({
  label,
  value,
  onChange,
  children,
}) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className={SELECT_CLASS}
      >
        {children}
      </select>
    </label>
  );
}

function ViewButton({
  active,
  icon: Icon,
  label,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        "inline-flex items-center justify-center gap-2",
        "rounded-lg px-3 py-2 text-xs font-medium transition",
        active
          ? "bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900"
          : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
      ].join(" ")}
    >
      <Icon />
      <span className="hidden sm:inline">
        {label}
      </span>
    </button>
  );
}

export function AssociationsFilters({
  filters,
  onFilterChange,
  onReset,

  products = [],
  disciplines = [],

  viewMode,
  onViewModeChange,

  totalCount = 0,
  activeFilterCount = 0,
  hasActiveFilters = false,
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
            <FaFilter className="text-violet-500" />
            Recherche et filtres

            {activeFilterCount > 0 ? (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-violet-100 px-1.5 text-[11px] font-bold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                {activeFilterCount}
              </span>
            ) : null}
          </div>

          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {totalCount.toLocaleString("fr-FR")} résultat
            {totalCount > 1 ? "s" : ""}
          </p>
        </div>

        <button
          type="button"
          onClick={onReset}
          disabled={!hasActiveFilters}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <FaTimes />
          Réinitialiser les filtres
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <label className="sm:col-span-2">
          <span className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
            Recherche globale
          </span>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="search"
              value={filters.search}
              onChange={(event) =>
                onFilterChange(
                  "search",
                  event.target.value,
                )
              }
              placeholder="Nom, email, téléphone, ville, discipline, contact…"
              className={`${SELECT_CLASS} pl-10 pr-10`}
            />

            {filters.search ? (
              <button
                type="button"
                onClick={() =>
                  onFilterChange("search", "")
                }
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label="Effacer la recherche"
              >
                <FaTimes className="text-xs" />
              </button>
            ) : null}
          </div>
        </label>

        <FilterSelect
          label="Produit"
          value={filters.product}
          onChange={(value) =>
            onFilterChange("product", value)
          }
        >
          <option value="">Tous les produits</option>

          {products.map((product) => (
            <option key={product} value={product}>
              {product}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label="Statut commercial"
          value={filters.status}
          onChange={(value) =>
            onFilterChange("status", value)
          }
        >
          <option value="">Tous les statuts</option>

          {ASSOCIATION_STATUS_VALUES.map(
            (status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ),
          )}
        </FilterSelect>

        <FilterSelect
          label="Discipline"
          value={filters.discipline}
          onChange={(value) =>
            onFilterChange("discipline", value)
          }
        >
          <option value="">
            Toutes les disciplines
          </option>

          {disciplines.map((discipline) => (
            <option
              key={discipline}
              value={discipline}
            >
              {discipline}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label="Favoris"
          value={filters.favorite}
          onChange={(value) =>
            onFilterChange("favorite", value)
          }
        >
          <option value="all">
            Tous les dossiers
          </option>
          <option value="yes">
            Favoris uniquement
          </option>
          <option value="no">
            Hors favoris
          </option>
        </FilterSelect>

        <FilterSelect
          label="Relances"
          value={filters.reminder}
          onChange={(value) =>
            onFilterChange("reminder", value)
          }
        >
          <option value="all">
            Toutes les relances
          </option>
          <option value="due">
            À traiter
          </option>
          <option value="upcoming">
            À venir
          </option>
          <option value="none">
            Sans relance
          </option>
        </FilterSelect>

        <FilterSelect
          label="Trier les résultats"
          value={filters.sortBy}
          onChange={(value) =>
            onFilterChange("sortBy", value)
          }
        >
          <option value="status">
            Parcours commercial
          </option>
          <option value="name">
            Nom alphabétique
          </option>
          <option value="product">
            Produit
          </option>
          <option value="reminder">
            Prochaine relance
          </option>
          <option value="recent">
            Ajouts récents
          </option>
        </FilterSelect>
      </div>

      <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Le mode d’affichage choisi est mémorisé sur
          cet appareil.
        </div>

        <div className="inline-flex w-fit items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-950/50">
          <ViewButton
            active={viewMode === "auto"}
            icon={FaDesktop}
            label="Auto"
            onClick={() =>
              onViewModeChange("auto")
            }
          />

          <ViewButton
            active={viewMode === "table"}
            icon={FaTable}
            label="Table"
            onClick={() =>
              onViewModeChange("table")
            }
          />

          <ViewButton
            active={viewMode === "cards"}
            icon={FaThLarge}
            label="Cartes"
            onClick={() =>
              onViewModeChange("cards")
            }
          />
        </div>
      </div>
    </section>
  );
}